import { useEffect } from "react";
import type { ReactNode } from "react";
import { cx } from "./utils";

/** One navigation destination. `to` is opaque — `renderLink` turns it into a link. */
export interface NavItem {
  to: string;
  label: string;
  /** Icon element (use `<Icon id="ui-…"/>`). */
  icon?: ReactNode;
  /** Marks the current destination. */
  active?: boolean;
  /** Small count badge (queue length, pending arbitration). */
  count?: number;
}

/** Signature of the router adapter AppShell needs, so `packages/ui` stays router-free. */
export type RenderLink = (args: {
  to: string;
  className: string;
  children: ReactNode;
  "aria-current": "page" | undefined;
  key?: string;
}) => ReactNode;

/** `system` follows `prefers-color-scheme` and keeps following it. */
export type ThemeMode = "light" | "dark" | "system";

export interface AppShellProps {
  /** Nav destinations. On mobile the first 4 become the bottom TabBar. */
  nav: readonly NavItem[];
  /**
   * Router adapter, e.g.
   * `({to, ...p}) => <NavLink to={to} {...p} />`. Falls back to `<a href>`.
   */
  renderLink?: RenderLink;
  /** Product name in the sidebar / top bar. */
  title?: ReactNode;
  /** Role switcher (and any other account controls) for the header. */
  roleSwitcher?: ReactNode;
  /** Extra header controls rendered to the right of the role switcher. */
  headerActions?: ReactNode;
  /** Offline / warning strip pinned under the header. */
  banner?: ReactNode;
  /**
   * Toggles `body[data-ds-dark-theme]`, which is how gizzai-design-system
   * switches palettes. @default "system"
   */
  theme?: ThemeMode;
  /** Page content. */
  children?: ReactNode;
  className?: string;
}

const defaultRenderLink: RenderLink = ({ to, className, children, ...rest }) => (
  <a href={to} className={className} {...rest}>
    {children}
  </a>
);

/**
 * Applies the design system's dark-theme attribute to `<body>`.
 * Exported so non-shell surfaces (login, print views) can reuse it.
 */
export function useDesignSystemTheme(mode: ThemeMode = "system"): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const body = document.body;
    const apply = (dark: boolean) => {
      if (dark) body.setAttribute("data-ds-dark-theme", "");
      else body.removeAttribute("data-ds-dark-theme");
    };
    if (mode !== "system") {
      apply(mode === "dark");
      return () => body.removeAttribute("data-ds-dark-theme");
    }
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    apply(!!mq?.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    mq?.addEventListener?.("change", onChange);
    return () => {
      mq?.removeEventListener?.("change", onChange);
      body.removeAttribute("data-ds-dark-theme");
    };
  }, [mode]);
}

/**
 * Responsive application frame: sidebar ≥1100px, compact top bar 768–1099px,
 * bottom tab bar <768px (first 4 items). All three layouts are rendered and
 * switched in CSS, so there is no JS resize dependency.
 */
export function AppShell({
  nav,
  renderLink = defaultRenderLink,
  title = "星海正音",
  roleSwitcher,
  headerActions,
  banner,
  theme = "system",
  children,
  className,
}: AppShellProps) {
  useDesignSystemTheme(theme);

  const link = (item: NavItem, kind: "side" | "top" | "tab") =>
    renderLink({
      key: `${kind}-${item.to}`,
      to: item.to,
      className: cx(`xz-nav__link xz-nav__link--${kind}`, item.active && "is-active"),
      "aria-current": item.active ? "page" : undefined,
      children: (
        <>
          {item.icon != null && (
            <span className="xz-nav__icon" aria-hidden="true">
              {item.icon}
            </span>
          )}
          <span className="xz-nav__label">{item.label}</span>
          {item.count != null && item.count > 0 && (
            <span className="xz-nav__count">{item.count}</span>
          )}
        </>
      ),
    });

  const tabs = nav.slice(0, 4);

  return (
    <div className={cx("xz-shell", className)}>
      <aside className="xz-shell__side">
        <div className="xz-shell__brand gz-type-heading">{title}</div>
        <nav className="xz-nav xz-nav--side" aria-label="主导航">
          {nav.map((i) => link(i, "side"))}
        </nav>
        {roleSwitcher != null && <div className="xz-shell__side-foot">{roleSwitcher}</div>}
      </aside>

      <div className="xz-shell__main">
        <header className="xz-shell__top">
          <span className="xz-shell__brand xz-shell__brand--top gz-type-heading">{title}</span>
          <nav className="xz-nav xz-nav--top" aria-label="主导航">
            {nav.map((i) => link(i, "top"))}
          </nav>
          <div className="xz-shell__top-actions">
            {roleSwitcher}
            {headerActions}
          </div>
        </header>

        {banner != null && <div className="xz-shell__banner">{banner}</div>}

        <main className="xz-shell__content" id="main">
          {children}
        </main>

        <nav className="xz-nav xz-nav--tabbar" aria-label="主导航">
          {tabs.map((i) => link(i, "tab"))}
        </nav>
      </div>
    </div>
  );
}
